import React from 'react';
import xmldoc from 'xmldoc';
import {isEmpty} from '../utilities/validation';
import {toCamelCase} from '../utilities/common';
import moment from 'moment';
import { BASE_URL, BASE_API_KEY } from '../config';

function evaluateActionScript(text, allowEmpty = false){
    while(text.indexOf('{{', 0) > 0 && text.indexOf('}}', 0) > 0) {
        let key = text.substring(text.indexOf('{{', 0)+2, text.indexOf('}}', 0));
        let newValue = '0';
        const startPos = text.indexOf('{{', 0)+2;
        const endPos = text.indexOf('}}', 0);

        while(key.indexOf('new Date()', 0) > 0) { // (MO.PERF.3.1.0 - 219)
            let value = `'${moment().format('MMMM Do YYYY')}'`; // extra set of quotes are required for this to be parsed in eval()
            key = key.replace('new Date()', value);
        }

        while(key.indexOf('currentTime.getMonth()', 0) > 0) { // (MO.PERF.3.1.0 - 219)
            let value = (moment().format('M') - 1).toString(); // The getMonth function in actionscript is indexed 1 month behind
            key = key.replace('currentTime.getMonth()', value);
        }

        while(key.indexOf('currentTime.getDate()', 0) > 0) { // (MO.PERF.3.1.0 - 219)
            let value = (moment().format('D')).toString();
            key = key.replace('currentTime.getDate()', value);
        }

        // Replacing HTML greater-than sign, when used in a condition (Immunology - 3.0.2, 119) (MO.FORC.3.0.2 - 224)
        key = key.replace(new RegExp('&amp;', 'g'), '&');

        // replace <br/> with a placeholder, so that we can safely swap <, >. It is used in evaluations.
        key = key.replace(new RegExp('&lt;br/&gt;', 'g'), '<br/>'); // (Immunology - 3.0.2, 119)
        // comment out <br> within actionscript code (it doesn't seem to have a use where it is present, and fails to evaluate too)
        key = key.replace(new RegExp('&lt;br&gt;', 'g'), '<br/>'); // (MO.PERF.3.1.0 - 219)

        // These math symbols (<, >) must be replaced in between the <br/> -> '&lt;br/&gt; swaps, so that the HTML's < > do not get replaced.
        // Replacing HTML greater-than sign, when used in a condition (Immunology - 3.0.2, 119) (MO.FORC.3.0.2 - 224)
        key = key.replace(new RegExp('&gt;', 'g'), '>'); // (MO.PERF.3.1.0 - 704)
        key = key.replace(new RegExp('&lt;', 'g'), '<');
        key = key.replace(new RegExp('&lt;=', 'g'), '<=');
        key = key.replace(new RegExp('&gt;=', 'g'), '>=');

        // returning <br> to its HTML-compatible state
        key = key.replace(new RegExp('<br/>', 'g'), '&lt;br/&gt;'); // (MO.PERF.3.1.0 - 219)

        try {
            let tempValue = eval(key);
            // if (typeof tempValue !== 'undefined' && !isEmpty(tempValue)){
            // we want to allow empty evaluated values, ie (MO.IMMG.3.0.2, 119 - "", " ")
            if (typeof tempValue !== 'undefined' && (allowEmpty || !isEmpty(tempValue))){
                newValue = tempValue;
            }
        } catch (e) {
            if (e instanceof SyntaxError) {
                // console.log(e.message);
                // console.log(key);
                // console.log(newValue);
            } else {
                throw (e);
            }
        }
        text = text.substr(0, startPos) + newValue + text.substr(endPos);
        text = text.replace('{{', '').replace('}}', '');
    }

    return text;
}

function swapInCDATA(text){
    while(text.indexOf('<*****', 0) > 0 || text.indexOf('*****>', 0) > 0) {
        text = text.replace('<*****', '<![CDATA[').replace('*****>', ']]>');
    }
    return text;
}

function swapOutCDATA(text){
    // Cleans CDATA from XML files
    while(text.indexOf('<![CDATA[', 0) > 0 || text.indexOf(']]>', 0) > 0) {
        text = text.replace('<![CDATA[', '<*****').replace(']]>', '*****>');
    }
    return text;
}

function parseAssignmentXML(text = '', assignment_info = null) {

    while(text.indexOf('{getCurrentDateTime()}', 0) > 0) {
        const key = '{getCurrentDateTime()}';
        let value = moment().format('MMMM Do YYYY');
        text = text.replace(key, value).replace('{getCurrentDateTime()}', '');
    }
    if(!isEmpty(text) && !isEmpty(assignment_info)){

        while(text.indexOf('{getCurrentUserName()}', 0) > 0) {
            const key = '{getCurrentUserName()}';
            let value = 'N/A';
            if(!isEmpty(assignment_info.users) && assignment_info.users.length > 0){
                value = `${assignment_info.users[0].name} (${assignment_info.users[0].username})`;
            }
            text = text.replace(key, value).replace('{getCurrentUserName()}', '');
        }
        while(text.indexOf('{getCurrentUserName(0)}', 0) > 0) {
            const key = '{getCurrentUserName(0)}';
            let value = 'N/A';
            if(!isEmpty(assignment_info.users) && assignment_info.users.length > 0){
                value = `${assignment_info.users[0].name} (${assignment_info.users[0].username})`;
            }
            text = text.replace(key, value).replace('{getCurrentUserName(0)}', '');
        }
        while(text.indexOf('{getCurrentUserName(1)}', 0) > 0) {
            const key = '{getCurrentUserName(1)}';
            let value = 'N/A';
            if(!isEmpty(assignment_info.users) && assignment_info.users.length > 1){
                value = `${assignment_info.users[1].name} (${assignment_info.users[1].username})`;
            }
            text = text.replace(key, value).replace('{getCurrentUserName(0)}', '');
        }
        while(text.indexOf('{getClassInfoStr()}', 0) > 0) {
            const key = '{getClassInfoStr()}';
            let value = 'N/A';
            if(!isEmpty(assignment_info.classroom) && !isEmpty(assignment_info.content)){
                value = `${assignment_info.classroom} - ${assignment_info.content}`;
            }
            text = text.replace(key, value).replace('{getClassInfoStr()}', '');
        }

        // Used for reports, removes remaining action script variables
        let lastIndexOfGetUserVar = 0;
        let indexOfOpen = text.indexOf('{', lastIndexOfGetUserVar);
        let indexOfClose = text.indexOf('}', lastIndexOfGetUserVar);

        while(indexOfOpen > 0 && indexOfClose > 0) {
            const key = text.substring(indexOfOpen+1, indexOfClose);
            if(!key.includes('getUserVar')) {
                text = text.replace(key, '').replace('{', '').replace('}', '');
            }
            else {
                lastIndexOfGetUserVar = indexOfClose+1;
            }
            indexOfOpen = text.indexOf('{', lastIndexOfGetUserVar);
            indexOfClose = text.indexOf('}', lastIndexOfGetUserVar);
        }
    }
    else {
        // Used for reports, removes remaining action script variables
        while(text.indexOf('{', 0) > 0 && text.indexOf('}', 0) > 0) {
            const key = text.substring(text.indexOf('{', 0)+1, text.indexOf('}', 0));
            text = text.replace(key, '').replace('{', '').replace('}', '');
        }
    }
    return text;
}

function parseXML(text, student_answers = null, assignment_info = null) {

    text = swapOutCDATA(text);

    // Replacing HTML quotation marks
    text = text.replace(new RegExp('&quot;', 'g'), '"'); // (MO.PERF.3.1.0 - 703)

    // Parse ActionScript Here
    if(!isEmpty(student_answers) && student_answers !== null) {
        // Replacing variables with student input if student input exists
        while(text.indexOf('[', 0) > 0 && text.indexOf(']', 0) > 0) {
            const startPos = text.indexOf('[', 0)+1;
            const endPos = text.indexOf(']', 0);
            const key = text.substring(text.indexOf('[', 0)+1, text.indexOf(']', 0));
            let value = 'null';
            if (!isEmpty(student_answers[toCamelCase(key)])) {
                value = student_answers[toCamelCase(key)];
            }
            else if (!isEmpty(student_answers[key])) {
                value = student_answers[key];
            }
            text = text.substr(0, startPos) + value + text.substr(endPos);

            text = text.replace('[', '').replace(']', '');
        }

        // Parsing action script functions into js and attempting to execute them
        text = evaluateActionScript(text, true);

        // Parsing the XML using the information from the current assignment
        text = parseAssignmentXML(text, assignment_info);
    }
    else {
        // Replacing student input variables with empty values
        while(text.indexOf('[', 0) > 0 && text.indexOf(']', 0) > 0) {
            const key = text.substring(text.indexOf('[', 0)+1, text.indexOf(']', 0));
            text = text.replace(key, '').replace('[', '').replace(']', '');
        }

        // Parsing action script functions into js and attempting to execute them
        text = evaluateActionScript(text);

        // Used for reports, gets the user's name, removes remaining action script variables
        while(text.indexOf('{', 0) > 0 && text.indexOf('}', 0) > 0) {
            const key = text.substring(text.indexOf('{', 0)+1, text.indexOf('}', 0));
            text = text.replace(key, '').replace('{', '').replace('}', '');
        }
    }

    return swapInCDATA(text);
}

function parsePrintXML(text, student_answers) {

    text = swapOutCDATA(text);

    // Parse ActionScript Here
    if(!isEmpty(student_answers)) {
        while (text.indexOf('{getUserVar(#', 0) > 0) {

            const key = text.substring(text.indexOf('{getUserVar(#', 0) + 13, text.indexOf('#)}', 0));
            let value = '';
            if (!isEmpty(student_answers[toCamelCase(key)])) {
                value = student_answers[toCamelCase(key)];
            } else if (!isEmpty(student_answers[key])) {
                value = student_answers[key];
            }

            const variableString = `{getUserVar(#${key}#)}`;
            text = text.replace(variableString, value);
        }
    }

    while(text.indexOf('{', 0) > 0 && text.indexOf('}', 0) > 0) {
        const key = text.substring(text.indexOf('{', 0)+1, text.indexOf('}', 0));
        text = text.replace(key, '').replace('{', '').replace('}', '');
    }

    return swapInCDATA(text);
}

function getDescendantsNamed(node, name, acc = []) {
    node.eachChild(child => {
        if (child.name === name) {
            acc.push(child);
        }
        getDescendantsNamed(child, name, acc);
    });
    return acc;
}

const fetchXML = function(file, answers = null, assignmentInfo = null) {
    // console.log("fetchXML");
    //var formData = new FormData();
    //formData.append('data', file);
    let payload = {
        deviceId: "49.205.111.56",
        userType: "nola-admin",
        developerKey: "yes",
        data: file
    };

    return new Promise((resolve, reject) => {
        //'http://itccontent.nolaedu.net/synergy-s3/moduleplay/jrexml.php'
        fetch(BASE_URL + '/module/player/book/content',{ method: 'POST', body: JSON.stringify(payload), headers: { 'api-key': BASE_API_KEY,
            'api-hash': BASE_API_KEY, 'Content-Type': 'application/json' } })
            .then(response => response.text())
            .then(text => {
                let xmlResp = JSON.parse(text);
                let temp_text = parseXML(xmlResp.result, answers, assignmentInfo);
                try {
                    const xml = new xmldoc.XmlDocument(temp_text);

                     // Assessmentname-start
                    // console.log("XML STRING:", temp_text);
                    // console.log("TYPE:", typeof temp_text);

                    // Get ALL <assessment_node> elements :-
                    const assessmentNodes = getDescendantsNamed(xml, "assessment_node");

                    assessmentNodes.forEach(node => {
                        // console.log("Assessment name:", node.attr.name);
                        localStorage.setItem("AssessmentName",node.attr.name)
                    });
                    // Assessmentname-end
                    
                    resolve(xml);
                } catch(e) {
                    reject(e);
                }
            })
            .catch(reject);
    });
};

function parseStudentAnswer(answers, variable) {
    // Loops through all provided student answers (usually fetched from the redux store) and comparing all of them with
    // the variable name of the node. If the student has previously submitted an answer that matches the name of the
    // node's variable, then the value will be set to the student's saved answer.
    for (const [key, value] of Object.entries(answers)) {
        // console.log('key: ' + key + " | value: " + value);
        if(key && !isEmpty(key) && !isEmpty(value) && (key.toLowerCase() === variable.toLowerCase())){
            return value;
        }
    }
    return '';
}

export {
    fetchXML,
    parsePrintXML,
    parseStudentAnswer
};



// WEBPACK FOOTER //
// ./src/js/utilities/xml.js